import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../components/ui/Modal.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Input } from '../../components/ui/Field.jsx'
import api from '../../lib/api.js'
import { formatCurrency } from '../../lib/format.js'

/*
 * Article picker — inventory §6. Debounced (300ms) search over GET /articles/search;
 * empty query loads a short default list (?limit=4). Selecting a row injects the
 * article into the current invoice line. Internal modal (no browser dialog).
 */
export default function ArticleSearchModal({ open, onClose, onSelect }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    setLoading(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const params = query.trim() ? { q: query.trim() } : { limit: 4 }
      api.get('/articles/search', { params })
        .then(({ data }) => setResults(data?.articles || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false))
    }, query.trim() ? 300 : 0)
    return () => clearTimeout(debounceRef.current)
  }, [query, open])

  useEffect(() => { if (!open) { setQuery(''); setResults([]) } }, [open])

  return (
    <Modal open={open} onClose={onClose} maxWidth={560}>
      <div style={{ padding: '22px 22px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17, color: 'var(--color-ink)' }}>
            {t('invoiceForm.articleSearch.title')}
          </h3>
          <button onClick={onClose} aria-label={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)' }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={17} /></span>
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('invoiceForm.articleSearch.placeholder')} style={{ paddingLeft: 38 }} />
        </div>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto', padding: '6px 12px 16px' }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : results.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('invoiceForm.articleSearch.empty')}</div>
        ) : results.map((a) => {
          const ratePct = a.vat_rate?.rate
          return (
            <button key={a.id} onClick={() => { onSelect(a); onClose() }} style={{
              display: 'flex', width: '100%', gap: 12, alignItems: 'center', textAlign: 'left',
              padding: '11px 12px', borderRadius: 10, border: '1px solid transparent', background: 'transparent',
            }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-lilac)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{a.code}</span>
                  {a.vat_rate?.label && <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{a.vat_rate.label}</span>}
                  {ratePct != null && <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{ratePct}%</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.description}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-title)', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatCurrency(a.unit_price)}</div>
            </button>
          )
        })}
      </div>
    </Modal>
  )
}
